import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, Lock, ShieldAlert } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_3adc9213-dab6-4851-9c35-a0c85e7e3dfd/artifacts/gaem0znj_Shoozix%20Signature.png";

const SIDE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3adc9213-dab6-4851-9c35-a0c85e7e3dfd/images/58af23d9684d3ebfe151f00158c74025ebcb80f78407499177520a01a2f0732e.png";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    // Simulate auth check, always fail (per brief)
    setTimeout(() => {
      setLoading(false);
      setError("Invalid credentials. Please verify your email and password.");
    }, 900);
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-[#F9F8F6]"
    >
      {/* Left — form */}
      <div className="relative flex flex-col p-8 md:p-16 lg:p-20">
        <Link
          to="/"
          data-testid="login-back-link"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#0F1B3D]/70 hover:text-[#0F1B3D] transition self-start"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to House
        </Link>

        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
          <img
            src={LOGO_URL}
            alt="Shoozix International LLP"
            data-testid="login-logo"
            className="h-16 w-auto mb-12 self-start"
            style={{ mixBlendMode: "multiply", filter: "contrast(1.1) brightness(1.02)" }}
          />

          <span className="text-[11px] tracking-[0.3em] uppercase text-[#B8945F] font-medium mb-4">
            Client Portal
          </span>
          <h1
            data-testid="login-headline"
            className="font-serif text-4xl md:text-5xl text-[#0F1B3D] leading-tight tracking-tight mb-3"
          >
            Welcome back.
          </h1>
          <p className="text-[14px] md:text-[15px] text-[#1A1A1A]/65 leading-relaxed mb-10 max-w-sm">
            Sign in to view your live samples, production timelines, dispatch updates and invoices.
          </p>

          <form onSubmit={onSubmit} className="space-y-7" data-testid="login-form">
            <div>
              <label
                htmlFor="email"
                className="block text-[10px] tracking-[0.3em] uppercase text-[#0F1B3D]/60 mb-2"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="you@brand.com"
                className="w-full bg-transparent border-b border-[#0F1B3D]/25 focus:border-[#B8945F] outline-none py-3 text-[15px] text-[#0F1B3D] placeholder:text-[#0F1B3D]/30 transition-colors"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-[10px] tracking-[0.3em] uppercase text-[#0F1B3D]/60 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  placeholder="••••••••••"
                  className="w-full bg-transparent border-b border-[#0F1B3D]/25 focus:border-[#B8945F] outline-none py-3 pr-10 text-[15px] text-[#0F1B3D] placeholder:text-[#0F1B3D]/30 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="login-toggle-password"
                  className="absolute right-0 bottom-3 text-[#0F1B3D]/50 hover:text-[#0F1B3D] transition"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-[12px]">
              <label className="inline-flex items-center gap-2 text-[#0F1B3D]/70 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="login-remember-checkbox"
                  className="appearance-none w-4 h-4 border border-[#0F1B3D]/30 checked:bg-[#0F1B3D] checked:border-[#0F1B3D] transition"
                />
                Remember me
              </label>
              <a
                href="#"
                data-testid="login-forgot-link"
                onClick={(e) => {
                  e.preventDefault();
                  setError("Password reset is disabled in this preview. Please contact exports@shoozix.in");
                }}
                className="text-[#0F1B3D]/70 hover:text-[#B8945F] transition"
              >
                Forgot password?
              </a>
            </div>

            {error && (
              <div
                data-testid="login-error-message"
                role="alert"
                className="flex items-start gap-3 p-4 bg-[#0F1B3D]/5 border-l-2 border-[#B33A3A] text-[#7F1D1D]"
              >
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-[13px] leading-relaxed">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="group w-full inline-flex items-center justify-center gap-3 bg-[#0F1B3D] text-[#F9F8F6] px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#B8945F] transition-colors duration-500 disabled:opacity-70"
            >
              <Lock className="w-3.5 h-3.5" strokeWidth={2} />
              <span>{loading ? "Verifying…" : "Sign In"}</span>
            </button>

            <p className="text-[12px] text-[#0F1B3D]/55 leading-relaxed pt-2">
              Not yet a client?{" "}
              <Link
                to="/#contact"
                data-testid="login-request-access"
                className="text-[#B8945F] link-underline"
              >
                Request access
              </Link>{" "}
              and our team will be in touch within two working days.
            </p>
          </form>
        </div>

        <p className="text-[10px] tracking-[0.25em] uppercase text-[#0F1B3D]/40">
          © {new Date().getFullYear()} Shoozix International LLP
        </p>
      </div>

      {/* Right — visual */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src={SIDE_IMG}
          alt="Premium leather materials"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F1B3D]/80 via-[#0F1B3D]/55 to-[#0F1B3D]/85" />
        <div className="relative h-full p-16 flex flex-col justify-between text-[#F9F8F6]">
          <span className="text-[11px] tracking-[0.3em] uppercase text-[#B8945F]">
            Private · Encrypted · For Clients
          </span>
          <div>
            <p className="font-serif text-4xl xl:text-5xl leading-[1.05] tracking-tight max-w-md">
              "Working with Shoozix feels like having a quiet, dependable atelier on retainer — except every pair leaves us better than we asked for."
            </p>
            <p className="mt-6 text-[12px] tracking-[0.2em] uppercase text-[#F9F8F6]/70">
              — Founder, European Private Label
            </p>
          </div>
          <div className="flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-[#F9F8F6]/60">
            <span>Agra · Noida</span>
            <span>Made in India</span>
          </div>
        </div>
      </div>
    </div>
  );
}
